import { useState } from "react";
import { motion } from "framer-motion";
import DOMPurify from "dompurify";
import { Copy, ClipboardCheck, Code2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { usePageMeta } from "../hooks/usePageMeta";

const EASE = [0.16, 1, 0.3, 1];

const inputCls =
  "w-full bg-transparent border-b border-steel/50 focus:border-tech-bright outline-none py-3 text-fog placeholder:text-fog/30 text-sm transition-colors duration-300";

const SigActions = ({ copyRich, copyHtml, showCode, html }) => (
  <>
    <div className="flex flex-wrap gap-4 pt-3">
      <button
        onClick={copyRich}
        data-testid="sig-copy-rich-button"
        className="group relative px-6 py-3.5 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
      >
        <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
        <span className="relative flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4" /> Copy signature
        </span>
      </button>
      <button
        onClick={copyHtml}
        data-testid="sig-copy-html-button"
        className="px-6 py-3.5 border border-steel text-fog/80 hover:border-tech-bright hover:text-tech-bright font-mono-accent text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 flex items-center gap-2"
      >
        <Code2 className="h-4 w-4" /> Copy HTML
      </button>
    </div>
    {showCode && (
      <textarea
        readOnly
        data-testid="sig-html-code-fallback"
        value={html}
        onFocus={(e) => e.target.select()}
        rows={6}
        className="w-full bg-navy-deep border border-steel/50 text-fog/70 text-[11px] font-mono-accent p-4 outline-none focus:border-tech-bright transition-colors duration-300"
      />
    )}
  </>
);

const SigField = ({ id, label, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label htmlFor={id} className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/40">{label}</label>
    <input id={id} data-testid={`${id}-input`} type={type} value={value} onChange={onChange} placeholder={placeholder} className={inputCls} />
  </div>
);

const SigPreview = ({ html }) => (
  <>
    <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-fog/40 mb-4">Live preview</p>
    <div className="bg-white p-8 md:p-10 border border-steel/30" data-testid="signature-preview">
      <div id="signature-preview-render" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
    </div>
    <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-fog/30 mt-4">
      Renders with your live logo — always up to date
    </p>
  </>
);

const esc = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildSignatureHtml = (raw) => {
  const telHref = raw.phone ? raw.phone.replace(/[^0-9+]/g, "") : "";
  const name = esc(raw.name);
  const title = esc(raw.title);
  const email = esc(raw.email);
  const phone = esc(raw.phone);
  const origin = window.location.origin;
  const logoUrl = `${origin}/assets/nwta-logo-full.png`;
  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;">
  <tr>
    <td style="vertical-align:middle;padding-right:18px;border-right:2px solid #2176C7;">
      <img src="${logoUrl}" width="160" alt="Northwest Tech Alliance" style="display:block;border:0;" />
    </td>
    <td style="vertical-align:middle;padding-left:18px;">
      <div style="font-size:16px;font-weight:bold;color:#0B2545;letter-spacing:0.5px;">${name}</div>
      <div style="font-size:11px;color:#3E5C76;letter-spacing:2px;text-transform:uppercase;padding-top:2px;">${title}</div>
      <div style="font-size:12px;color:#0B2545;padding-top:8px;">
        ${phone ? `<a href="tel:${telHref}" style="color:#0B2545;text-decoration:none;">${phone}</a><span style="color:#3E5C76;"> &nbsp;·&nbsp; </span>` : ""}<a href="mailto:${email}" style="color:#2176C7;text-decoration:none;">${email}</a>
      </div>
      <div style="font-size:11px;color:#2176C7;letter-spacing:3px;padding-top:6px;">AI &middot; SOFTWARE &middot; INNOVATION</div>
      <div style="font-size:10px;color:#8CA0B3;padding-top:6px;">
        Northwest Title Alliance, DBA Northwest Tech Alliance &middot;
        <a href="${origin}" style="color:#2176C7;text-decoration:none;"> ${origin.replace(/^https?:\/\//, "")}</a> &middot;
        <a href="https://northwesttitlealliance.com/" style="color:#2176C7;text-decoration:none;">northwesttitlealliance.com</a>
      </div>
    </td>
  </tr>
</table>`;
};

const SignatureGenerator = () => {
  usePageMeta({ title: "Email Signature Generator", description: "Generate a branded Northwest Tech Alliance email signature in seconds.", path: "/signature" });
  const [form, setForm] = useState({
    name: "",
    title: "",
    email: "",
    phone: "(253) 453-1754",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [showCode, setShowCode] = useState(false);

  const preview = {
    ...form,
    name: form.name || "Your Name",
    title: form.title || "Your Title",
    email: form.email || "you@nwta.app",
  };
  const html = buildSignatureHtml(preview);

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      toast.success("HTML code copied — paste it into your email client's signature settings.");
    } catch {
      setShowCode(true);
      toast.info("Select and copy the code below the buttons.");
    }
  };

  const copyRich = async () => {
    try {
      const blobHtml = new Blob([html], { type: "text/html" });
      const blobText = new Blob([`${preview.name} — ${preview.title} | Northwest Tech Alliance | ${preview.email}${preview.phone ? " | " + preview.phone : ""}`], { type: "text/plain" });
      await navigator.clipboard.write([new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText })]);
      toast.success("Signature copied — paste it straight into Gmail or Outlook.");
    } catch {
      const range = document.createRange();
      const node = document.getElementById("signature-preview-render");
      range.selectNodeContents(node);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand("copy");
      sel.removeAllRanges();
      toast.success("Signature copied — paste it straight into Gmail or Outlook.");
    }
  };

  return (
    <main className="bg-navy min-h-screen">
      <Navbar />
      <section className="relative pt-36 md:pt-44 pb-24 md:pb-32 grain" data-testid="signature-generator">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mb-14 md:mb-20"
          >
            <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">Team Tool</span>
            <h1 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog mt-6">
              Email signature<span className="text-tech">.</span>
              <br />
              <span className="text-fog/40">Generated in seconds.</span>
            </h1>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              className="md:col-span-5"
            >
              <div className="grid grid-cols-1 gap-7" data-testid="signature-form">
                <SigField id="sig-name" label="Full Name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                <SigField id="sig-title" label="Job Title" value={form.title} onChange={set("title")} placeholder="Director of Underwriting" />
                <SigField id="sig-email" label="Email" type="email" value={form.email} onChange={set("email")} placeholder="jane@nwta.app" />
                <SigField id="sig-phone" label="Phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(253) 453-1754" />

                <SigActions copyRich={copyRich} copyHtml={copyHtml} showCode={showCode} html={html} />

                <p className="text-xs text-fog/40 leading-relaxed pt-2"><Copy className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />
                  Gmail: Settings → Signature → paste. Outlook: Settings → Mail →
                  Compose and reply → paste. Use "Copy HTML" for clients that ask for raw code.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
              className="md:col-span-7"
            >
              <SigPreview html={html} />
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default SignatureGenerator;
